import {
  PRIVATE_MEDIA_BUCKET,
  createPrivateMediaStorage
} from '../platform/private-media-storage.js';
import {
  isOwnedSmokerPhotoPath,
  planSmokerPhotoRemoval,
  planSmokerPhotoReplacement,
  validateSmokerPhotoFile
} from './smoker-photo.js';

export const SMOKER_PHOTO_BUCKET=PRIVATE_MEDIA_BUCKET;

function safeError(error,code){
  if(error instanceof TypeError)throw error;
  const wrapped=new Error(code);
  wrapped.code=String(error?.code||code).slice(0,80);
  wrapped.cause=error;
  return wrapped;
}

export function createSmokerPhotoStorage({
  client,
  signedUrlTtlSeconds=3600
}={}){
  const media=createPrivateMediaStorage({client,signedUrlTtlSeconds});

  async function upload({userId,smokerId,currentPath=null,file,mimeType,sizeBytes,localPreviewUri=null}={}){
    validateSmokerPhotoFile({mimeType,sizeBytes});
    if(file==null)throw new TypeError('Photo file is required');

    const plan=planSmokerPhotoReplacement({
      userId,
      smokerId,
      currentPath,
      mimeType,
      sizeBytes,
      localPreviewUri
    });

    try{
      await media.upload({
        userId,
        path:plan.uploadPath,
        file,
        mimeType:plan.mimeType,
        sizeBytes:plan.sizeBytes,
        upsert:true
      });
    }catch(error){
      throw safeError(error,'SMOKER_PHOTO_UPLOAD_FAILED');
    }

    return Object.freeze({
      ok:true,
      path:plan.uploadPath,
      deleteAfterUpload:plan.deleteAfterUpload,
      localPreviewUri:plan.localPreviewUri
    });
  }

  async function remove({userId,smokerId,currentPath}={}){
    const plan=planSmokerPhotoRemoval({userId,smokerId,currentPath});
    if(!plan.deletePath)return Object.freeze({ok:true,status:'already_absent'});

    try{
      await media.remove({userId,paths:[plan.deletePath]});
    }catch(error){
      throw safeError(error,'SMOKER_PHOTO_DELETE_FAILED');
    }
    return Object.freeze({ok:true,status:'deleted',path:plan.deletePath});
  }

  async function cleanup(path){
    const value=String(path||'').trim();
    if(!value)return Object.freeze({ok:true,status:'nothing_to_clean'});
    const userId=value.split('/')[0]||'';
    try{
      await media.remove({userId,paths:[value]});
    }catch(error){
      throw safeError(error,'SMOKER_PHOTO_CLEANUP_FAILED');
    }
    return Object.freeze({ok:true,status:'cleaned',path:value});
  }

  async function signedUrl({userId,smokerId,path}={}){
    const value=String(path||'').trim();
    if(!value)return null;
    if(!isOwnedSmokerPhotoPath(value,{userId,smokerId})){
      throw new TypeError('Smoker photo path is outside the owner scope');
    }
    try{
      return await media.signedUrl({userId,path:value});
    }catch(error){
      throw safeError(error,'SMOKER_PHOTO_URL_FAILED');
    }
  }

  return Object.freeze({upload,remove,cleanup,signedUrl});
}
